import type { ReactElement } from 'react';
import { BiRadioCircleMarked } from 'react-icons/bi';
import type { IconBaseProps } from 'react-icons/lib';
import { LuFlaskConical, LuLayers2, LuWeight } from 'react-icons/lu';
import { TbRulerMeasure } from 'react-icons/tb';

import { PropertyGroup, PropertyItem } from '~components';
import { ScrollArea } from '~components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~components/ui/sheet';
import type { ColourByParameter, OptimisationDrillHoleProperties } from '~types';
import { formatNumericWithUnit } from '~utils';
import { PARAMETERS_LABEL_COLOURS } from '../config';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type OptimiserDrillHoleDetailsProps = {
  /** The drill hole data to display */
  drillHoleData: OptimisationDrillHoleProperties | null;
  /** The container element to render the drawer into */
  container?: HTMLElement;
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when the drawer is closed */
  onClose: () => void;
};

type DisplayItemProp<T> = {
  /** */
  icon?: ReactElement<IconBaseProps>;
  /** Parameter label */
  label: string;
  /** Function to get the parameter value from the data */
  getValue: (data: T) => string;
  /** Optional unit to display alongside the value */
  unit?: string;
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Gets the display label for a hole parameter
 * @param parameter - The parameter to get the label for
 * @returns Human-readable label for the parameter
 */
const getParameterLabel = (parameter: ColourByParameter): string =>
  PARAMETERS_LABEL_COLOURS.find((p) => p.value === parameter)?.label || 'Unknown';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

/**
 * Drill hole charge design parameters
 */
const CHARGE_DESIGN_PARAMETERS: DisplayItemProp<NonNullable<OptimisationDrillHoleProperties['charge_design']>>[] = [
  {
    icon: <LuFlaskConical size={14} />,
    label: 'Explosive Density',
    getValue: (d) => formatNumericWithUnit(d.density, 2),
    unit: 'g/cm³',
  },
  {
    icon: <TbRulerMeasure size={14} />,
    label: 'Stemming Length',
    getValue: (d) => formatNumericWithUnit(d.stem, 2),
    unit: 'm',
  },
  { icon: <LuWeight size={14} />, label: 'MIC', getValue: (d) => formatNumericWithUnit(d.mic, 1), unit: 'kg' },
  { icon: <LuLayers2 size={14} />, label: 'SDoB', getValue: (d) => formatNumericWithUnit(d.sdob, 1), unit: 'm/kg⅓' },
];

/**
 * Drill hole properties
 */
const DRILL_HOLE_PARAMETERS: DisplayItemProp<OptimisationDrillHoleProperties>[] = [
  {
    label: getParameterLabel('diameter'),
    getValue: (d) => formatNumericWithUnit(d.diameter ? d.diameter * 1000 : undefined, 0),
    unit: 'mm',
  },
  { label: getParameterLabel('depth'), getValue: (d) => formatNumericWithUnit(d.depth, 2), unit: 'm' },
  { label: getParameterLabel('burden'), getValue: (d) => formatNumericWithUnit(d.burden, 2), unit: 'm' },
  { label: getParameterLabel('spacing'), getValue: (d) => formatNumericWithUnit(d.spacing, 2), unit: 'm' },
  { label: getParameterLabel('bearing'), getValue: (d) => formatNumericWithUnit(d.bearing, 0), unit: '°' },
  { label: getParameterLabel('dip'), getValue: (d) => formatNumericWithUnit(d.dip, 0), unit: '°' },
  { label: getParameterLabel('blast_mj'), getValue: (d) => formatNumericWithUnit(d.blast_mj, 1), unit: 'MJ' },
  { label: getParameterLabel('crush_mj'), getValue: (d) => formatNumericWithUnit(d.crush_mj, 1), unit: 'MJ' },
  { label: getParameterLabel('dig_mj'), getValue: (d) => formatNumericWithUnit(d.dig_mj, 1), unit: 'MJ' },
  { label: getParameterLabel('drill_mj'), getValue: (d) => formatNumericWithUnit(d.drill_mj, 1), unit: 'MJ' },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sheet component to display details of an optimisation drill hole, including charge design parameters and hole properties.
 *
 * @example
 * ```tsx
 *   <OptimiserDrillHoleDetails
 *     drillHoleData={selectedHoleData}
 *     container={drawerContainer}
 *     open={isDrawerOpen}
 *     onClose={() => setIsDrawerOpen(false)}
 *   />
 * ```
 */
const OptimiserDrillHoleDetails = ({ drillHoleData, container, open, onClose }: OptimiserDrillHoleDetailsProps) => {
  const chargeDesign = drillHoleData?.charge_design;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        portalContainer={container ?? document.body}
        side="inline-end"
        strategy="absolute"
        showOverlay={false}
        className="grow border-t-0"
        data-open={open}
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          {drillHoleData ? (
            <SheetTitle className="flex items-center gap-2">
              <BiRadioCircleMarked className="rounded-full bg-primary/10 p-1 text-primary" size={32} />
              Hole: <span>{drillHoleData.hole_name}</span>
            </SheetTitle>
          ) : (
            <SheetTitle>Hole Details</SheetTitle>
          )}
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="h-full w-full" type="auto">
            {drillHoleData && (
              <div className="space-y-6 p-6">
                {chargeDesign && (
                  <PropertyGroup withBorder>
                    {CHARGE_DESIGN_PARAMETERS.map(({ icon, label, getValue, unit }) => (
                      <PropertyItem
                        key={label}
                        icon={icon}
                        label={label}
                        value={getValue(chargeDesign)}
                        unit={unit}
                        variant="featured"
                      />
                    ))}
                  </PropertyGroup>
                )}
                <PropertyGroup>
                  {DRILL_HOLE_PARAMETERS.map(({ label, getValue, unit }) => (
                    <PropertyItem key={label} label={label} value={getValue(drillHoleData)} unit={unit} contained />
                  ))}
                </PropertyGroup>
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { OptimiserDrillHoleDetails };
